import { compressImage } from '@markku/components'
import { useCallback, useState } from 'react'
import { trpc } from '../trpc'
import { AttachmentAction } from '../trpcHooks/attachment'

export interface AttachmentInfo {
  id: number
  filename: string
  filesize: number
  mimeType?: string
  status: 'existing' | 'pending' | 'uploading' | 'error'
  error?: string
  progress?: number
  downloadUrl?: string
}

interface UseAttachmentsReturn {
  attachments: AttachmentInfo[]
  isUploading: boolean
  uploadFile: (file: File) => Promise<void>
  removeAttachment: (id: number) => void
  getChanges: () => AttachmentAction[]
  setInitialAttachments: (attachments: AttachmentInfo[]) => void
}

/**
 * Generic hook for managing attachments with deferred linking
 * Uploads happen immediately, but linking/unlinking happens on entity save
 * @param entityType - Optional entity type for the attachments
 * @param initialAttachments - Existing attachments when editing an entity
 */
export function useAttachments(
  entityType?: string,
  initialAttachments?: any[],
): UseAttachmentsReturn {
  // Initialize attachments with existing ones if provided
  const [attachments, setAttachments] = useState<AttachmentInfo[]>(() => {
    if (initialAttachments && initialAttachments.length > 0) {
      return initialAttachments.map((a) => ({
        id: a.id,
        filename: a.filename,
        filesize: a.filesize,
        mimeType: a.mimeType,
        status: 'existing' as const,
        downloadUrl: a.downloadUrl,
      }))
    }
    return []
  })
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set())
  const [addedIds, setAddedIds] = useState<Set<number>>(new Set())
  const [isUploading, setIsUploading] = useState(false)

  // Use tRPC mutation for uploading
  const uploadMutation = trpc.attachment.createAttachment.useMutation()

  // Set initial attachments (for editing existing entities)
  const setInitialAttachments = useCallback(
    (initialAttachments: AttachmentInfo[]) => {
      setAttachments(
        initialAttachments.map((a) => ({
          ...a,
          status: 'existing' as const,
        })),
      )
      setRemovedIds(new Set())
      setAddedIds(new Set())
    },
    [],
  )

  // Upload a file immediately (without entity linkage)
  const uploadFile = useCallback(
    async (file: File) => {
      // Compress image if it's an image file
      const fileToUpload = await compressImage(file)

      // Create temporary attachment info
      const tempId = Date.now()
      const tempAttachment: AttachmentInfo = {
        id: tempId,
        filename: fileToUpload.name,
        filesize: fileToUpload.size,
        mimeType: fileToUpload.type,
        status: 'uploading',
        progress: 0,
      }

      // Add to list immediately
      setAttachments((prev) => [...prev, tempAttachment])
      setIsUploading(true)

      try {
        // Upload without entityId (will be linked on save)
        const result = await uploadMutation.mutateAsync({
          filename: fileToUpload.name,
          fileSize: fileToUpload.size,
          bucket: 'private',
          entityType, // Optional, can be undefined
        })

        // Upload to S3
        const formData = new FormData()
        Object.entries(result.fields).forEach(([key, value]) => {
          formData.append(key, value as string)
        })
        formData.append('file', fileToUpload)

        const uploadResponse = await fetch(result.uploadUrl, {
          method: 'POST',
          body: formData,
        })

        if (!uploadResponse.ok) {
          throw new Error('Upload failed')
        }

        // Update attachment with real ID
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === tempId
              ? {
                  ...a,
                  id: result.id,
                  status: 'pending' as const,
                  progress: 100,
                }
              : a,
          ),
        )

        // Track as added
        setAddedIds((prev) => new Set([...prev, result.id]))
      } catch (error) {
        // Update with error status
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === tempId
              ? {
                  ...a,
                  status: 'error' as const,
                  error:
                    error instanceof Error ? error.message : 'Upload failed',
                }
              : a,
          ),
        )
      } finally {
        setIsUploading(false)
      }
    },
    [entityType, uploadMutation],
  )

  // Remove attachment (mark for deletion on save)
  const removeAttachment = useCallback(
    (id: number) => {
      setAttachments((prev) => {
        const attachment = prev.find((a) => a.id === id)
        if (!attachment) return prev

        // If it was added in this session, just remove from added list
        if (addedIds.has(id)) {
          setAddedIds((ids) => {
            const newIds = new Set(ids)
            newIds.delete(id)
            return newIds
          })
          return prev.filter((a) => a.id !== id)
        }

        // If it's an existing attachment, mark for removal
        if (attachment.status === 'existing') {
          setRemovedIds((ids) => new Set([...ids, id]))
        }

        // Remove from UI
        return prev.filter((a) => a.id !== id)
      })
    },
    [addedIds],
  )

  // Get changes for submission
  const getChanges = useCallback((): AttachmentAction[] => {
    const changes: AttachmentAction[] = []

    // Add actions for newly added attachments
    addedIds.forEach((id) => {
      changes.push({ id, action: 'add' })
    })

    // Add actions for removed attachments
    removedIds.forEach((id) => {
      changes.push({ id, action: 'delete' })
    })

    return changes
  }, [addedIds, removedIds])

  return {
    attachments: attachments.filter((a) => a.status !== 'error'),
    isUploading,
    uploadFile,
    removeAttachment,
    getChanges,
    setInitialAttachments,
  }
}
